import {
  StateNode,
  Vec,
  createShapeId,
  debugFlags,
  kickoutOccludedShapes,
  pointInPolygon,
  toRichText,
  unsafe__withoutCapture
} from "@tldraw/editor";
import { isOverArrowLabel } from "../../../shapes/arrow/arrowLabel.mjs";
import { getHitShapeOnCanvasPointerDown } from "../../selection-logic/getHitShapeOnCanvasPointerDown.mjs";
import { updateHoveredOverlayId } from "../../selection-logic/updateHoveredOverlayId.mjs";
import {
  cancelUpdateHoveredShapeId,
  updateHoveredShapeId
} from "../../selection-logic/updateHoveredShapeId.mjs";
import { hasRichText, startEditingShapeWithRichText } from "../selectHelpers.mjs";
const SKIPPED_KEYS_FOR_AUTO_EDITING = [
  "Delete",
  "Backspace",
  "[",
  "]",
  "Enter",
  " ",
  "Shift",
  "Tab"
];
class Idle extends StateNode {
  static id = "idle";
  selectedShapesOnKeyDown = [];
  onEnter() {
    this.parent.setCurrentToolIdMask(void 0);
    this.editor.setCursor({ type: "default", rotation: 0 });
    if (!updateHoveredOverlayId(this.editor)) {
      updateHoveredShapeId(this.editor);
    }
    this.selectedShapesOnKeyDown = [];
  }
  onExit() {
    this.editor.updateInstanceState({ isChangingStyle: false });
    cancelUpdateHoveredShapeId(this.editor);
  }
  onPointerMove() {
    if (!updateHoveredOverlayId(this.editor)) {
      updateHoveredShapeId(this.editor);
    }
    if (unsafe__withoutCapture(() => this.editor.getInstanceState()).isChangingStyle) {
      this.editor.updateInstanceState({ isChangingStyle: false });
    }
  }
  onPointerDown(info) {
    switch (info.target) {
      case "canvas": {
        const currentPagePoint = this.editor.inputs.getCurrentPagePoint();
        const hitOverlay = this.editor.overlays.getOverlayAtPoint(
          currentPagePoint,
          this.editor.getHitTestMargin()
        );
        if (hitOverlay) {
          this.onPointerDown({
            ...info,
            target: "overlay",
            overlay: hitOverlay
          });
          return;
        }
        const hitShape = getHitShapeOnCanvasPointerDown(this.editor);
        if (hitShape && (this.editor.options.selectLockedShapes || !hitShape.isLocked)) {
          this.onPointerDown({
            ...info,
            shape: hitShape,
            target: "shape"
          });
          return;
        }
        const selectedShapeIds = this.editor.getSelectedShapeIds();
        const onlySelectedShape = this.editor.getOnlySelectedShape();
        if (selectedShapeIds.length > 1 || onlySelectedShape && !this.editor.getShapeUtil(onlySelectedShape).hideSelectionBoundsBg(onlySelectedShape)) {
          if (isPointInRotatedSelectionBounds(this.editor, currentPagePoint)) {
            this.onPointerDown({
              ...info,
              target: "selection"
            });
            return;
          }
        }
        this.parent.transition("pointing_canvas", info);
        break;
      }
      case "overlay": {
        const { overlay } = info;
        const util = this.editor.overlays.getOverlayUtil(overlay);
        if (util.onPointerDown) {
          const result = util.onPointerDown(overlay, info);
          if (result !== false) return;
        }
        const overlayType = overlay.props.overlayType;
        if (overlay.type === "shape_handle") {
          const shape = this.editor.getShape(overlay.props.shapeId);
          if (shape) {
            this.onPointerDown({
              ...info,
              target: "handle",
              shape,
              handle: overlay.props.handle
            });
          }
        } else {
          switch (overlayType) {
            case "rotate_handle": {
              this.onPointerDown({
                ...info,
                target: "selection",
                handle: overlay.props.handle
              });
              break;
            }
            case "mobile_rotate": {
              this.onPointerDown({
                ...info,
                target: "selection",
                handle: overlay.props.handle
              });
              break;
            }
            case "resize_handle": {
              this.onPointerDown({
                ...info,
                target: "selection",
                handle: overlay.props.handle
              });
              break;
            }
            default: {
              this.onPointerDown({
                ...info,
                target: "selection"
              });
            }
          }
        }
        break;
      }
      case "shape": {
        const { shape } = info;
        if (!this.editor.options.selectLockedShapes && this.editor.isShapeOrAncestorLocked(shape)) {
          this.parent.transition("pointing_canvas", info);
          break;
        }
        this.parent.transition("pointing_shape", info);
        break;
      }
      case "handle": {
        if (this.editor.getIsReadonly()) break;
        if (this.editor.inputs.getAltKey()) {
          this.parent.transition("pointing_shape", info);
        } else {
          this.parent.transition("pointing_handle", info);
        }
        break;
      }
      case "selection": {
        switch (info.handle) {
          case "mobile_rotate":
          case "top_left_rotate":
          case "top_right_rotate":
          case "bottom_left_rotate":
          case "bottom_right_rotate": {
            if (info.accelKey) {
              this.parent.transition("brushing", info);
              break;
            }
            this.parent.transition("pointing_rotate_handle", info);
            break;
          }
          case "top":
          case "right":
          case "bottom":
          case "left":
          case "top_left":
          case "top_right":
          case "bottom_left":
          case "bottom_right": {
            const onlySelectedShape = this.editor.getOnlySelectedShape();
            if (info.ctrlKey && this.editor.canCropShape(onlySelectedShape)) {
              this.parent.transition("crop.pointing_crop_handle", info);
            } else {
              if (info.accelKey) {
                this.parent.transition("brushing", info);
                break;
              }
              this.parent.transition("pointing_resize_handle", info);
            }
            break;
          }
          default: {
            const hoveredShape = this.editor.getHoveredShape();
            if (hoveredShape && !this.editor.getSelectedShapeIds().includes(hoveredShape.id) && (this.editor.options.selectLockedShapes || !hoveredShape.isLocked)) {
              this.onPointerDown({
                ...info,
                shape: hoveredShape,
                target: "shape"
              });
              return;
            }
            this.parent.transition("pointing_selection", info);
          }
        }
        break;
      }
    }
  }
  onDoubleClick(info) {
    if (this.editor.inputs.getShiftKey() || info.phase !== "down") return;
    if (info.ctrlKey || info.shiftKey) return;
    switch (info.target) {
      case "canvas": {
        const currentPagePoint = this.editor.inputs.getCurrentPagePoint();
        const hitOverlay = this.editor.overlays.getOverlayAtPoint(
          currentPagePoint,
          this.editor.getHitTestMargin()
        );
        if (hitOverlay) {
          if (hitOverlay.type === "shape_handle") {
            const shape = this.editor.getShape(hitOverlay.props.shapeId);
            if (shape) {
              this.onDoubleClick({
                ...info,
                target: "handle",
                shape,
                handle: hitOverlay.props.handle
              });
              return;
            }
          }
          const overlayType = hitOverlay.props.overlayType;
          if (overlayType === "resize_handle" || overlayType === "rotate_handle" || overlayType === "mobile_rotate") {
            this.onDoubleClick({
              ...info,
              target: "selection",
              handle: hitOverlay.props.handle
            });
            return;
          }
        }
        const hoveredShape = this.editor.getHoveredShape();
        const hitShape = hoveredShape && !this.editor.isShapeOfType(hoveredShape, "group") ? hoveredShape : this.editor.getSelectedShapeAtPoint(currentPagePoint) ?? this.editor.getShapeAtPoint(currentPagePoint, {
          margin: this.editor.getHitTestMargin(),
          hitInside: false
        });
        if (hitShape) {
          this.onDoubleClick({
            ...info,
            shape: hitShape,
            target: "shape"
          });
          return;
        }
        if (!this.editor.inputs.getShiftKey()) {
          this.handleDoubleClickOnCanvas(info);
        }
        break;
      }
      case "selection": {
        const onlySelectedShape = this.editor.getOnlySelectedShape();
        if (onlySelectedShape) {
          const util = this.editor.getShapeUtil(onlySelectedShape);
          const isEdge = info.handle === "right" || info.handle === "left" || info.handle === "top" || info.handle === "bottom";
          const isCorner = info.handle === "top_left" || info.handle === "top_right" || info.handle === "bottom_right" || info.handle === "bottom_left";
          if (this.editor.getIsReadonly()) {
            if (this.editor.canEditShape(onlySelectedShape, {
              type: isCorner ? "double-click-corner" : isEdge ? "double-click-edge" : "double-click"
            })) {
              this.startEditingShape(
                onlySelectedShape,
                info,
                true
                /* select all */
              );
            }
            break;
          }
          if (isEdge) {
            const change = util.onDoubleClickEdge?.(onlySelectedShape, info);
            if (change) {
              this.editor.markHistoryStoppingPoint("double click edge");
              this.editor.updateShapes([change]);
              kickoutOccludedShapes(this.editor, [onlySelectedShape.id]);
              return;
            }
          }
          if (isCorner) {
            const change = util.onDoubleClickCorner?.(onlySelectedShape, info);
            if (change) {
              this.editor.markHistoryStoppingPoint("double click corner");
              this.editor.updateShapes([change]);
              kickoutOccludedShapes(this.editor, [onlySelectedShape.id]);
              return;
            }
          }
          if (this.editor.canCropShape(onlySelectedShape)) {
            this.parent.transition("crop", info);
            return;
          }
          if (this.editor.canEditShape(onlySelectedShape)) {
            this.startEditingShape(
              onlySelectedShape,
              info,
              true
              /* select all */
            );
          }
        }
        break;
      }
      case "shape": {
        const { shape } = info;
        const selectedShapeIds = this.editor.getSelectedShapeIds();
        const isGroup = this.editor.isShapeOfType(shape, "group");
        if (isGroup || this.editor.getOutermostSelectableShape(shape).id !== shape.id) {
          const shapeToSelect = this.editor.getOutermostSelectableShape(
            shape,
            (parent) => !selectedShapeIds.includes(parent.id)
          );
          if (!selectedShapeIds.includes(shapeToSelect.id)) {
            this.editor.markHistoryStoppingPoint("drilling into group on double click");
            this.editor.select(shapeToSelect.id);
          }
          return;
        }
        const util = this.editor.getShapeUtil(shape);
        if (shape.type !== "video" && shape.type !== "embed" && this.editor.getIsReadonly()) break;
        if (util.onDoubleClick) {
          const change = util.onDoubleClick?.(shape);
          if (change) {
            this.editor.updateShapes([change]);
            return;
          }
        }
        if (util.canCrop(shape) && !this.editor.isShapeOrAncestorLocked(shape)) {
          this.editor.markHistoryStoppingPoint("select and crop");
          this.editor.select(info.shape?.id);
          this.parent.transition("crop", info);
          return;
        }
        if (this.editor.canEditShape(shape)) {
          this.startEditingShape(
            shape,
            info,
            true
            /* select all */
          );
        } else {
          this.handleDoubleClickOnCanvas(info);
        }
        break;
      }
      case "handle": {
        if (this.editor.getIsReadonly()) break;
        const { shape, handle } = info;
        const util = this.editor.getShapeUtil(shape);
        const changes = util.onDoubleClickHandle?.(shape, handle);
        if (changes) {
          this.editor.updateShapes([changes]);
        } else {
          if (this.editor.canEditShape(shape)) {
            this.startEditingShape(
              shape,
              info,
              true
              /* select all */
            );
          }
        }
      }
    }
  }
  onRightClick(info) {
    switch (info.target) {
      case "canvas": {
        const selectedShapeIds = this.editor.getSelectedShapeIds();
        const onlySelectedShape = this.editor.getOnlySelectedShape();
        const currentPagePoint = this.editor.inputs.getCurrentPagePoint();
        if (selectedShapeIds.length > 1 || onlySelectedShape && !this.editor.getShapeUtil(onlySelectedShape).hideSelectionBoundsBg(onlySelectedShape)) {
          if (isPointInRotatedSelectionBounds(this.editor, currentPagePoint)) {
            this.onRightClick({
              ...info,
              target: "selection"
            });
            return;
          }
        }
        const hoveredShape = this.editor.getHoveredShape();
        const hitShape = hoveredShape && !this.editor.isShapeOfType(hoveredShape, "group") ? hoveredShape : this.editor.getShapeAtPoint(currentPagePoint, {
          margin: this.editor.getHitTestMargin(),
          hitInside: false,
          hitLabels: true,
          hitLocked: true,
          hitFrameInside: true,
          renderingOnly: true
        });
        if (hitShape) {
          this.onRightClick({
            ...info,
            shape: hitShape,
            target: "shape"
          });
          return;
        }
        this.editor.selectNone();
        break;
      }
      case "shape": {
        const { selectedShapeIds } = this.editor.getCurrentPageState();
        const { shape } = info;
        const targetShape = this.editor.getOutermostSelectableShape(
          shape,
          (parent) => !selectedShapeIds.includes(parent.id)
        );
        if (!selectedShapeIds.includes(targetShape.id) && !this.editor.findShapeAncestor(
          targetShape,
          (shape2) => selectedShapeIds.includes(shape2.id)
        )) {
          this.editor.markHistoryStoppingPoint("selecting shape");
          this.editor.setSelectedShapes([targetShape.id]);
        }
        break;
      }
    }
  }
  onCancel() {
    if (this.editor.getFocusedGroupId() !== this.editor.getCurrentPageId() && this.editor.getSelectedShapeIds().length > 0) {
      this.editor.popFocusedGroupId();
    } else {
      this.editor.markHistoryStoppingPoint("clearing selection");
      this.editor.selectNone();
    }
  }
  onKeyDown(info) {
    this.selectedShapesOnKeyDown = this.editor.getSelectedShapes();
    switch (info.code) {
      case "ArrowLeft":
      case "ArrowRight":
      case "ArrowUp":
      case "ArrowDown": {
        if (info.accelKey) {
          if (info.shiftKey) {
            if (info.code === "ArrowDown") {
              this.editor.selectFirstChildShape();
            } else if (info.code === "ArrowUp") {
              this.editor.selectParentShape();
            }
          } else {
            this.editor.selectAdjacentShape(
              info.code.replace("Arrow", "").toLowerCase()
            );
          }
          return;
        }
        this.nudgeSelectedShapes(false);
        return;
      }
    }
    if (debugFlags["editOnType"].get()) {
      if (!SKIPPED_KEYS_FOR_AUTO_EDITING.includes(info.key) && !info.altKey && !info.ctrlKey) {
        const onlySelectedShape = this.editor.getOnlySelectedShape();
        if (onlySelectedShape && // If it's a note shape, then edit on type
        this.editor.isShapeOfType(onlySelectedShape, "note") && // If it's not locked or anything
        this.editor.canEditShape(onlySelectedShape)) {
          this.startEditingShape(
            onlySelectedShape,
            {
              ...info,
              target: "shape",
              shape: onlySelectedShape
            },
            true
            /* select all */
          );
          return;
        }
      }
    }
  }
  onKeyRepeat(info) {
    switch (info.code) {
      case "ArrowLeft":
      case "ArrowRight":
      case "ArrowUp":
      case "ArrowDown": {
        if (info.accelKey) {
          this.editor.selectAdjacentShape(
            info.code.replace("Arrow", "").toLowerCase()
          );
          return;
        }
        this.nudgeSelectedShapes(true);
        break;
      }
      case "Tab": {
        const selectedShapes = this.editor.getSelectedShapes();
        if (selectedShapes.length && !info.altKey) {
          this.editor.selectAdjacentShape(info.shiftKey ? "prev" : "next");
        }
        break;
      }
    }
  }
  onKeyUp(info) {
    switch (info.key) {
      case "Enter": {
        if (!this.selectedShapesOnKeyDown.length) return;
        const selectedShapes = this.editor.getSelectedShapes();
        if (selectedShapes.every((shape) => this.editor.isShapeOfType(shape, "group"))) {
          this.editor.setSelectedShapes(
            selectedShapes.flatMap((shape) => this.editor.getSortedChildIdsForParent(shape.id))
          );
          return;
        }
        const onlySelectedShape = this.editor.getOnlySelectedShape();
        if (onlySelectedShape && this.editor.canEditShape(onlySelectedShape, { type: "press_enter" })) {
          this.startEditingShape(
            onlySelectedShape,
            {
              ...info,
              target: "shape",
              shape: onlySelectedShape
            },
            true
            /* select all */
          );
          return;
        }
        if (this.editor.canCropShape(onlySelectedShape)) {
          this.parent.transition("crop", info);
        }
        break;
      }
      case "Tab": {
        const selectedShapes = this.editor.getSelectedShapes();
        if (selectedShapes.length && !info.altKey) {
          this.editor.selectAdjacentShape(info.shiftKey ? "prev" : "next");
        }
        break;
      }
    }
  }
  startEditingShape(shape, info, shouldSelectAll) {
    const { editor } = this;
    this.editor.markHistoryStoppingPoint("editing shape");
    if (hasRichText(shape)) {
      startEditingShapeWithRichText(editor, shape, { selectAll: shouldSelectAll });
    } else {
      editor.setEditingShape(shape);
    }
    this.parent.transition("editing_shape", info);
  }
  isOverArrowLabelTest(shape) {
    if (!shape) return false;
    return isOverArrowLabel(this.editor, shape);
  }
  handleDoubleClickOnCanvas(info) {
    if (this.editor.getIsReadonly()) return;
    if (!this.editor.options.createTextOnCanvasDoubleClick) return;
    this.editor.markHistoryStoppingPoint("creating text shape");
    const id = createShapeId();
    const { x, y } = this.editor.inputs.getCurrentPagePoint();
    this.editor.createShapes([
      {
        id,
        type: "text",
        x,
        y,
        props: {
          richText: toRichText(""),
          autoSize: true
        }
      }
    ]);
    const shape = this.editor.getShape(id);
    if (!shape) return;
    if (!this.editor.canEditShape(shape)) return;
    startEditingShapeWithRichText(this.editor, id, { info });
  }
  nudgeSelectedShapes(ephemeral = false) {
    const {
      editor: {
        inputs: { keys }
      }
    } = this;
    const shiftKey = keys.has("ShiftLeft");
    const delta = new Vec(0, 0);
    if (keys.has("ArrowLeft")) delta.x -= 1;
    if (keys.has("ArrowRight")) delta.x += 1;
    if (keys.has("ArrowUp")) delta.y -= 1;
    if (keys.has("ArrowDown")) delta.y += 1;
    if (delta.equals(new Vec(0, 0))) return;
    if (!ephemeral) this.editor.markHistoryStoppingPoint("nudge shapes");
    this.editor.updateInstanceState({ isChangingStyle: true });
    const { gridSize } = this.editor.getDocumentSettings();
    const step = this.editor.getInstanceState().isGridMode ? shiftKey ? gridSize * GRID_INCREMENT : gridSize : shiftKey ? MAJOR_NUDGE_FACTOR : MINOR_NUDGE_FACTOR;
    const selectedShapeIds = this.editor.getSelectedShapeIds();
    this.editor.nudgeShapes(selectedShapeIds, delta.mul(step));
    kickoutOccludedShapes(this.editor, selectedShapeIds);
  }
}
const MAJOR_NUDGE_FACTOR = 10;
const MINOR_NUDGE_FACTOR = 1;
const GRID_INCREMENT = 5;
function isPointInRotatedSelectionBounds(editor, point) {
  const selectionBounds = editor.getSelectionRotatedPageBounds();
  if (!selectionBounds) return false;
  const selectionRotation = editor.getSelectionRotation();
  if (!selectionRotation) return selectionBounds.containsPoint(point);
  return pointInPolygon(
    point,
    selectionBounds.corners.map((c) => Vec.RotWith(c, selectionBounds.point, selectionRotation))
  );
}
export {
  GRID_INCREMENT,
  Idle,
  MAJOR_NUDGE_FACTOR,
  MINOR_NUDGE_FACTOR
};
//# sourceMappingURL=Idle.mjs.map
